import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, Facebook, Youtube, MessageCircle, Music2, X } from 'lucide-react';
import LegalModal from '@/components/LegalModal';
import { privacyPolicy, termsAndConditions, aiDisclaimer } from '@/lib/legalContent';
import { servicePages } from '@/components/services/servicePages';
import { useAuth } from '@/lib/AuthContext';
import { useLocation } from 'react-router-dom';
import { localeFromPath, privacyPath } from '@/lib/locales';
import CountrySelector from '@/components/locale/CountrySelector';
import useWhatsApp from '@/lib/useWhatsApp';
import { generalWaMessage } from '@/components/services/servicesData';

const footerLinks = [
{ label: 'About', href: '/about' },
{ label: 'Founder', href: '/founder' },
{ label: 'Prices', href: '/prices' },
{ label: 'Partnerships', href: '/partners' },
{ label: 'Blog', href: '/blog' },
{ label: 'Contact', href: '/contact' }];

const countryLinks = [
{ label: 'Global / UAE', href: '/' },
{ label: 'United States', href: '/us' },
{ label: 'Canada', href: '/ca' },
{ label: 'Australia', href: '/au' },
{ label: 'España', href: '/es' },
{ label: 'México', href: '/mx' }];


const socialLinks = [
{ icon: Linkedin, href: 'https://www.linkedin.com/in/jotunheimsfzco', label: 'LinkedIn' },
{ icon: Instagram, href: 'https://www.instagram.com/jotunheimsfzco', label: 'Instagram' },
{ icon: Facebook, href: 'https://www.facebook.com/share/18qogdRhKQ/', label: 'Facebook' },
{ icon: Youtube, href: 'https://www.youtube.com/@jotunheimscompany', label: 'YouTube' },
{ icon: Music2, href: 'https://www.tiktok.com/@jotunheimsfzco', label: 'TikTok' },
{ icon: X, href: 'https://x.com/jotunheimsfzco', label: 'X' },
{ icon: MessageCircle, href: 'https://wa.me/971504977224', label: 'WhatsApp' }];


export default function Footer() {
  const [legalModal, setLegalModal] = useState(null);
  const { isAuthenticated } = useAuth();
  const loc = localeFromPath(useLocation().pathname);
  const es = loc?.lang === 'es';
  const { waLink, t } = useWhatsApp();
  const wa = waLink(generalWaMessage);

  return (
    <footer className="relative border-t border-border/30">
      {/* Energy line animation */}
      <div className="absolute top-0 left-0 w-full h-px overflow-hidden">
        <motion.div
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 6, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }}
          className="absolute w-full h-full bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
        
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://media.base44.com/images/public/69f7328a281ea086a55ced65/e73f4b1ea_Untitled-2.png"
                alt="Jotunheims logo"
                width="36" height="36"
                className="w-9 h-9 object-contain" />
              
              <span className="font-inter font-bold text-lg tracking-widest uppercase text-foreground">
                Jotunheims
              </span>
            </div>
            <p className="text-sm font-inter font-bold tracking-[0.15em] uppercase text-foreground mb-5">
              Powering Innovation, Empowering You
            </p>
            <address className="not-italic text-xs text-muted-foreground font-inter tracking-wider leading-relaxed mb-5">
              Jotunheims FZCO · Dubai Silicon Oasis, Dubai, United Arab Emirates<br />
              WhatsApp: <a href={wa} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">+971 50 497 7224</a><br />
              <a href="mailto:info@jotunheims.com" className="hover:text-primary transition-colors">info@jotunheims.com</a>
            </address>

            {/* Social Icons */}
            <div className="flex items-center gap-1.5 mb-6 flex-nowrap">
              {socialLinks.map(({ icon: Icon, href, label }) =>
              <a
                key={label}
                href={label === 'WhatsApp' ? wa : href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Jotunheims on ${label}`}
                className="w-7 h-7 shrink-0 flex items-center justify-center rounded-md border border-border/40 text-muted-foreground hover:text-primary hover:border-primary/50 transition-all duration-300">
                
                  <Icon className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            {/* LinkedIn personal */}
            









            
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-xs font-inter font-bold tracking-[0.2em] uppercase text-foreground mb-6">
              {es ? 'Empresa' : 'Company'}
            </h3>
            <div className="space-y-3">
              {footerLinks.map((link) =>
              link.href.startsWith('/') ?
              <Link
                key={link.label}
                to={link.href}
                className="block text-sm text-muted-foreground font-inter hover:text-primary transition-colors">
                
                    {t(link.label)}
                  </Link> :

              <a
                key={link.label}
                href={link.href}
                className="block text-sm text-muted-foreground font-inter hover:text-primary transition-colors">
                
                    {link.label}
                  </a>

              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs font-inter font-bold tracking-[0.2em] uppercase text-foreground mb-6">
              {es ? 'Servicios' : 'Services'}
            </h3>
            <div className="space-y-3">
              {servicePages.map((s) =>
              <Link key={s.slug} to={`/services/${s.slug}`} className="block text-sm text-muted-foreground font-inter hover:text-primary transition-colors">
                  {s.name}
                </Link>
              )}
            </div>
          </div>

          {/* Countries */}
          <div>
            <h3 className="text-xs font-inter font-bold tracking-[0.2em] uppercase text-foreground mb-6">
              {es ? 'Países' : 'Countries'}
            </h3>
            <div className="space-y-3">
              {countryLinks.map((c) =>
              <Link key={c.href} to={c.href} className="block text-sm text-muted-foreground font-inter hover:text-primary transition-colors">
                  {c.label}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-border/20 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground font-inter tracking-wider">
            © {new Date().getFullYear()} Jotunheims FZCO. {es ? 'Todos los derechos reservados.' : 'All rights reserved.'}

          </span>
          <div className="flex flex-col lg:flex-row items-center gap-2 lg:gap-4 lg:whitespace-nowrap">
            <div className="flex items-center gap-4">
              {loc ?
              <Link
                to={privacyPath(loc.cc)}
                className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground hover:text-primary transition-colors">
                
                  {loc.privacyLabel}
                </Link> :

              <button
                onClick={() => setLegalModal('privacy')}
                className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground hover:text-primary transition-colors">
                
                  Privacy Policy
                </button>
              }
              <button
                onClick={() => setLegalModal('terms')}
                className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground hover:text-primary transition-colors">
                
                Terms &amp; Conditions
              </button>
            </div>
            <button
              onClick={() => setLegalModal('ai')}
              className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground hover:text-primary transition-colors">
              
              AI Technology Disclaimer and Liability Limitation
            </button>
          </div>
          <div className="flex items-center gap-2">
            
            

            
          </div>
        </div>
      </div>

      {legalModal === 'privacy' &&
      <LegalModal
        title={privacyPolicy.title}
        effectiveDate={privacyPolicy.effectiveDate}
        sections={privacyPolicy.sections}
        onClose={() => setLegalModal(null)} />

      }
      {legalModal === 'terms' &&
      <LegalModal
        title={termsAndConditions.title}
        effectiveDate={termsAndConditions.effectiveDate}
        sections={termsAndConditions.sections}
        onClose={() => setLegalModal(null)} />

      }
      {legalModal === 'ai' &&
      <LegalModal
        title={aiDisclaimer.title}
        effectiveDate={aiDisclaimer.effectiveDate}
        sections={aiDisclaimer.sections}
        onClose={() => setLegalModal(null)} />

      }
    </footer>);

}